<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();
useHead({
  titleTemplate: () => t("seo.layouts./register-bot.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./register-bot.description") },
    {
      property: "og:title", content: () => t("seo.layouts./register-bot.title")
    },
    { property: "og:description", content: () => t("seo.layouts./register-bot.description") },
    {
      property: "og:image",
      content: "/images/chatbot.png",
    },
    { property: "og:image:width", content: "2668" },
    { property: "og:image:height", content: "2000" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
});

const popularQuestions = ref([
  {
    question: t('registerBotLanding.faq[0].question'),
    answer: t('registerBotLanding.faq[0].answer'),
    isOpen: false
  },
  {
    question: t('registerBotLanding.faq[1].question'),
    answer: t('registerBotLanding.faq[1].answer'),
    isOpen: false
  },
  {
    question: t('registerBotLanding.faq[2].question'),
    answer: t('registerBotLanding.faq[2].answer'),
    isOpen: false
  },
  {
    question: t('registerBotLanding.faq[3].question'),
    answer: t('registerBotLanding.faq[3].answer'),
    isOpen: false
  },
])

const scrollToForm = () => {
  const el = document.querySelector('#registration-form')
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})
</script>

<template>
  <div>
    <!-- Hero Banner -->
    <div class="main-banner">
      <div class="main-banner-content !justify-center gap-8">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('registerBotLanding.hero.title') }}
          </div>
          <div class="banner-description">
            {{ $t('registerBotLanding.hero.subtitle') }}
          </div>
          <div class="banner-action">
            <button @click="scrollToForm" class="button filled whitespace-nowrap !text-sm">
              <p>{{ $t('registerBotLanding.hero.cta') }}</p>
            </button>
            <NuxtLink to="viber://chat?service=26076" class="button outlined" external>
              {{ $t('actions.contactUs') }}
            </NuxtLink>
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg preload src="/images/chatbot.png" sizes="xs:100vw sm:100vw md:50vw lg:600px xl:800px xxl:1000px" :alt="$t('seo.layouts./register-bot.title')" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" class="w-full h-auto object-contain" />
        </div>
      </div>
    </div>

    <!-- Process Steps Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('registerBotLanding.process.title') }}</div>
          <p class="mt-4 text-gray-600">{{ $t('registerBotLanding.process.subtitle') }}</p>
        </div>
        <div class="section-content">
          <div class="process-steps">
            <div class="process-step">
              <div class="step-number">1</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t('registerBotLanding.process.steps[0].title') }}</h3>
                <p class="step-description">{{ $t('registerBotLanding.process.steps[0].description') }}</p>
              </div>
            </div>
            <div class="process-step">
              <div class="step-number">2</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t('registerBotLanding.process.steps[1].title') }}</h3>
                <p class="step-description">{{ $t('registerBotLanding.process.steps[1].description') }}</p>
              </div>
            </div>
            <div class="process-step">
              <div class="step-number">3</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t('registerBotLanding.process.steps[2].title') }}</h3>
                <p class="step-description">{{ $t('registerBotLanding.process.steps[2].description') }}</p>
              </div>
            </div>
            <div class="process-step">
              <div class="step-number">4</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t('registerBotLanding.process.steps[3].title') }}</h3>
                <p class="step-description">{{ $t('registerBotLanding.process.steps[3].description') }}</p>
              </div>
            </div>
            <div class="process-step">
              <div class="step-number">5</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t('registerBotLanding.process.steps[4].title') }}</h3>
                <p class="step-description">{{ $t('registerBotLanding.process.steps[4].description') }}</p>
              </div>
            </div>
            <div class="process-step">
              <div class="step-number">6</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t('registerBotLanding.process.steps[5].title') }}</h3>
                <p class="step-description">{{ $t('registerBotLanding.process.steps[5].description') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section service-domain" id="pricing">
      <div class="container">
        <div class="service-price">
          <div class="price-label">{{ $t('global.price') }}</div>
        </div>
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('registerBotLanding.pricing.title') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-cards">
            <!-- Registration Fee -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>{{ $t('registerBotLanding.pricing.registration.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">90 EUR</span>
                  <span class="price-note">{{ $t('registerBotLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('registerBotLanding.pricing.registration.description') }}</p>
              </div>
            </div>

            <!-- Prepayment -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>{{ $t('registerBotLanding.pricing.prepayment.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">300 EUR</span>
                  <span class="price-note">{{ $t('registerBotLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('registerBotLanding.pricing.prepayment.description') }}</p>
              </div>
            </div>
          </div>

          <!-- Traffic Rates Info -->
          <div class="traffic-info">
            <div class="traffic-info-content">
              <span class="pi pi-info-circle text-2xl text-blue-500"></span>
              <div>
                <h4>{{ $t('registerBotLanding.pricing.traffic.title') }}</h4>
                <p>{{ $t('registerBotLanding.pricing.traffic.description') }}</p>
                <NuxtLink :to="localePath('/viber-chatbots') + '#rates'" class="traffic-link">
                  {{ $t('registerBotLanding.pricing.traffic.link') }}
                  <span class="pi pi-external-link ml-1"></span>
                </NuxtLink>
              </div>
            </div>
          </div>

          <!-- Total to Start -->
          <div class="total-start">
            <div class="total-start-content">
              <h3>{{ $t('registerBotLanding.pricing.total.title') }}</h3>
              <div class="total-amount">
                <span class="total-price">390 EUR</span>
                <span class="total-note">{{ $t('registerBotLanding.pricing.vatNote') }}</span>
              </div>
              <p class="total-breakdown">{{ $t('registerBotLanding.pricing.total.breakdown') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Commercial Bot Usage Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('registerBotLanding.commercial.title') }}</div>
          <p class="mt-4 text-gray-600">{{ $t('registerBotLanding.commercial.subtitle') }}</p>
        </div>
        <div class="section-content">
          <div class="commercial-card">
            <div class="commercial-card-content">
              <div class="commercial-icon">
                <span class="pi pi-calendar text-4xl"></span>
              </div>
              <div class="commercial-details">
                <h3>{{ $t('registerBotLanding.pricing.monthly.title') }}</h3>
                <p class="commercial-description">{{ $t('registerBotLanding.pricing.monthly.description') }}</p>
              </div>
              <div class="commercial-price">
                <span class="price">100 EUR</span>
                <span class="price-period">/ {{ $t('global.month') }}</span>
                <span class="price-note">{{ $t('registerBotLanding.pricing.vatNote') }}</span>
              </div>
            </div>
            <div class="commercial-note">
              <span class="pi pi-info-circle mr-2"></span>
              {{ $t('registerBotLanding.pricing.monthly.startDate') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Registration Form Section -->
    <div class="page-section" id="registration-form">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('registerBotLanding.form.title') }}</div>
          <p class="mt-4 text-gray-600">{{ $t('registerBotLanding.form.subtitle') }}</p>
        </div>
        <div class="section-content">
          <RegisterBotForm class="shadow-card mx-auto !p-8 max-w-[600px]" />
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="page-section">
      <div class="container">
        <ClientOnly>
          <PopularQuestions :title="$t('registerBotLanding.faq.title')" :questions="popularQuestions" />
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.banner-image-container {
  width: 100%;
  margin-top: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
}

@media (max-width: 900px) {
  .banner-image-container {
    margin-top: 50px;
  }
}

@media (max-width: 850px) {
  .main-banner-content {
    display: block;
  }
}

.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}

/* Process Steps */
.process-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.process-step {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.process-step:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.step-number {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3070b6, #044068);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.step-description {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Pricing Cards */
.pricing-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.pricing-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  border: 2px solid transparent;
  transition: transform 0.2s, box-shadow 0.2s;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.pricing-card.featured {
  border-color: #7c3aed;
  background: linear-gradient(180deg, #faf5ff 0%, white 100%);
}

.pricing-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #3070b6, #044068);
  color: white;
  padding: 0.375rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.pricing-card-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.pricing-card-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.pricing-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.price-period {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 400;
}

.price-note {
  font-size: 0.75rem;
  color: #9ca3af;
}

.pricing-card-body {
  text-align: center;
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1.6;
}

.pricing-note {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #fef3c7;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #92400e;
}

/* Traffic Info */
.traffic-info {
  margin-top: 2rem;
  background: #eff6ff;
  border-radius: 12px;
  padding: 1.5rem;
}

.traffic-info-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.traffic-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.traffic-info p {
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.traffic-link {
  color: #7c3aed;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
}

.traffic-link:hover {
  color: #5b21b6;
}

/* Total to Start */
.total-start {
  margin-top: 2rem;
  background: linear-gradient(135deg, #3070b6, #044068);
  border-radius: 16px;
  padding: 2rem;
  color: white;
}

.total-start-content {
  text-align: center;
}

.total-start h3 {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.total-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.total-price {
  font-size: 3rem;
  font-weight: 700;
}

.total-note {
  font-size: 0.85rem;
  opacity: 0.8;
}

.total-breakdown {
  font-size: 0.95rem;
  opacity: 0.9;
}

/* Form Section */
#registration-form {
  background: #f9fafb;
}

/* Commercial Card */
.commercial-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  max-width: 700px;
  margin: 2rem auto 0;
}

.commercial-card-content {
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
}

.commercial-icon {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #3070b6, #044068);
  color: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.commercial-details {
  flex: 1;
}

.commercial-details h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.commercial-description {
  color: #6b7280;
  font-size: 0.95rem;
}

.commercial-price {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.commercial-price .price {
  font-size: 2rem;
}

.commercial-note {
  background: #fef3c7;
  color: #92400e;
  padding: 1rem 2rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .commercial-card-content {
    flex-direction: column;
    text-align: center;
  }

  .commercial-details {
    text-align: center;
  }
}
</style>
